"use client";

/**
 * useResolverStep — one POST to /api/actions/resolver/next per collected change.
 *
 * The server applies auto-advance patches and returns merged `collected` plus
 * optional prefetched target rows. We keep resolved collected in local state
 * for RunStep so we do not re-fetch after server auto-advance.
 */

import { useEffect, useRef, useState } from "react";

import type {
  CollectedValues,
  PrefetchedContact,
  StepDescriptor,
} from "@/lib/actions/resolution/types";
import {
  isResolverNextApiResponse,
} from "@/lib/actions/resolution/apiTypes";

import { useResolverContext } from "./ResolverContext";

type State =
  | { status: "loading" }
  | {
      status: "ready";
      step: StepDescriptor;
      runCollected: CollectedValues;
      prefetchedTargets?: PrefetchedContact[];
    }
  | { status: "error"; message: string };

function serverHasAdvanceOverClient(
  client: CollectedValues,
  server: CollectedValues,
): boolean {
  for (const [index, id] of Object.entries(server.picks)) {
    if (id && client.picks[Number(index)] !== id) {
      return true;
    }
  }
  if (server.bulkPick?.ids?.length && !client.bulkPick?.ids?.length) {
    return true;
  }
  return false;
}

export function useResolverStep(): State {
  const { collected, mergeServerCollected } = useResolverContext();
  const [state, setState] = useState<State>({ status: "loading" });
  const lastSyncedCollected = useRef<CollectedValues | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    void (async () => {
      try {
        const res = await fetch("/api/actions/resolver/next", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collected }),
        });
        const payload: unknown = await res.json();
        if (cancelled) return;

        if (!isResolverNextApiResponse(payload)) {
          const message =
            payload &&
            typeof payload === "object" &&
            "message" in payload &&
            typeof (payload).message === "string"
              ? (payload as { message: string }).message
              : "Invalid resolver response";
          setState({ status: "error", message });
          return;
        }

        const { step, collected: serverCollected, prefetchedTargets } = payload;

        if (step.kind === "error") {
          setState({
            status: "error",
            message: step.message ?? "Unknown error",
          });
          return;
        }

        const runCollected: CollectedValues = serverCollected;

        if (
          serverCollected !== lastSyncedCollected.current &&
          serverHasAdvanceOverClient(collected, serverCollected)
        ) {
          lastSyncedCollected.current = serverCollected;
          mergeServerCollected(serverCollected);
        }

        setState({
          status: "ready",
          step,
          runCollected,
          prefetchedTargets,
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Network error";
        setState({ status: "error", message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collected, mergeServerCollected]);

  return state;
}
